import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { HubConnection } from '@microsoft/signalr';

import dayjs from 'dayjs';
import { counter } from '@store/reducers/reminderSlice';
import { useUsers } from '@shared/hooks/useUsers';
import { SERVICES } from '@shared/constants/apiConfig';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initiateWSConnection } from '@shared/commonFunctions';

import { HeaderControlsWrapper, HeaderNotificationsWrapper, UserSelector } from '@styles/Layout/HeaderStyles';
import { Roles } from '@shared/constants/constants';
import { selectViewAsUserId, viewAsUserIdAction } from '../../../../../../store/reducers/headerSlice';
import { SMSNotifications } from './SMSNotifications';
// import { LenderNotifications } from './LenderNotifications';
import { IReminders, RemindersPreview } from './popovers/RemindersPreview';
import { DealStatusRemindersPreview, IDealStatusReminders } from './popovers/DealStatusRemindersPreview';

export const HeaderControls = () => {
  const dispatch = useAppDispatch();
  const viewAs = useAppSelector(selectViewAsUserId);
  const reminderCounter = useAppSelector(counter);
  const { userId, isAdmin, userHasRoles, userRoles } = useUserAccount();
  const [reminders, setReminders] = useState<IReminders[]>([]);
  const [localCounter, setLocalCounter] = useState(0);
  const [dealStatusReminders, setDealStatusReminders] = useState<IDealStatusReminders[]>([]);
  const [lenderNotificationConnection, setLenderNotificationConnection] = useState<HubConnection>();
  const { data: allReminders, mutate: reloadReminders } = useSWR(userId && `/${SERVICES.NOTIFICATION}/reminders/${userId}`);
  const { data: allDealStatusReminders, mutate: reloadDealStatusReminders } = useSWR(
    userId && `/${SERVICES.NOTIFICATION}/deal-lender-status-notification/${userId}`,
    {
      refreshInterval: 5 * 60 * 1000,
      revalidateOnFocus: true,
    }
  );

  const { users } = useUsers(true);

  const userSelectOptions = useMemo(
    () =>
      users?.groupUsers?.map((user) => ({
        value: user.userId,
        label: user.userName,
      })) || [],
    [users]
  );

  const canViewAs = useMemo(() => {
    if (isAdmin || userHasRoles([Roles.LC_LEAD, Roles.LC_MANAGER])) {
      return true;
    }
    return false;
  }, [isAdmin, userRoles]);
  let timeoutId: NodeJS.Timeout;
  useEffect(() => {
    const currentDate = dayjs(new Date().toISOString());
    const futureReminders = allReminders?.filter(
      (reminder: IReminders) =>
        reminder.actionDate == null && dayjs(new Date(reminder.reminderDate).toISOString()).isAfter(currentDate)
    );

    if (futureReminders?.length > 0) {
      timeoutId && clearTimeout(timeoutId);
      const nearestFutureReminder = futureReminders[futureReminders.length - 1];
      const diff = dayjs(new Date(nearestFutureReminder.reminderDate).toISOString()).diff(currentDate);
      timeoutId = setTimeout(() => {
        setLocalCounter((prev) => prev + 1);
      }, diff);
    }
  }, [allReminders?.length]);

  useEffect(() => {
    reloadReminders();
    reloadDealStatusReminders();
  }, [reminderCounter]);

  const onLenderNotificationReceive = useCallback(() => {
    reloadDealStatusReminders();
  }, [reloadDealStatusReminders]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const url = `${axios.defaults.baseURL}/hubs/lender-notification`;
    let currentConnection: HubConnection | undefined;

    const initiateAndStartConnection = async () => {
      try {
        const newConnection = await initiateWSConnection(url);
        await newConnection.start();
        currentConnection = newConnection;
        setLenderNotificationConnection(newConnection);
      } catch (error) {
        console.error('Lender notification connection failed: ', error);
      }
    };

    initiateAndStartConnection();

    return () => {
      if (currentConnection && currentConnection.state === 'Connected') {
        setTimeout(() => {
          currentConnection?.stop();
        }, 1000);
      }
    };
  }, [userId]);

  useEffect(() => {
    if (lenderNotificationConnection && lenderNotificationConnection.state === 'Connected' && userId) {
      lenderNotificationConnection.on('Receive', onLenderNotificationReceive);
      lenderNotificationConnection.invoke('SubscribeByContactUser', userId);
    }

    return () => {
      if (lenderNotificationConnection && lenderNotificationConnection.state === 'Connected' && userId) {
        lenderNotificationConnection.off('Receive');
        lenderNotificationConnection.invoke('UnsubscribeFromContactUser', userId);
      }
    };
  }, [lenderNotificationConnection, lenderNotificationConnection?.state, onLenderNotificationReceive, userId]);

  useEffect(() => {
    const currentDate = dayjs(new Date().toISOString());
    const currentReminders = allReminders?.filter(
      (reminder: IReminders) =>
        reminder.actionDate == null && dayjs(new Date(reminder.reminderDate).toISOString()).isBefore(currentDate)
    );
    setReminders(currentReminders);
  }, [localCounter, allReminders]);

  useEffect(() => {
    const currentDate = dayjs(new Date().toISOString());
    const currentDealStatusReminders = allDealStatusReminders?.filter(
      (reminder: IDealStatusReminders) =>
        reminder.actionDate == null && dayjs(new Date(reminder.reminderDate).toISOString()).isBefore(currentDate)
    );
    setDealStatusReminders(currentDealStatusReminders);
  }, [allDealStatusReminders]);

  return (
    <HeaderControlsWrapper>
      <HeaderNotificationsWrapper>
        <RemindersPreview
          reminders={reminders}
          reloadReminders={reloadReminders}
        />
        <SMSNotifications />
        <DealStatusRemindersPreview
          reminders={dealStatusReminders}
          reloadReminders={reloadDealStatusReminders}
        />
        {/* <LenderNotifications /> */}
      </HeaderNotificationsWrapper>

      {canViewAs && (
        <UserSelector>
          View As
          <SelectAutocomplete
            removeExtraPadding
            value={viewAs.userId}
            placeholder="Select User"
            options={userSelectOptions}
            onChange={(userId: string) => dispatch(viewAsUserIdAction({ userId, refreshMenu: true }))}
          />
        </UserSelector>
      )}
    </HeaderControlsWrapper>
  );
};
